import { useEffect, useRef, useState } from "react";
import { HERO_VIDEOS } from "../data/content";

// Two distinct orderings so both sides don't look parallel
const LEFT_VIDEOS = HERO_VIDEOS;
const RIGHT_VIDEOS = [...HERO_VIDEOS.slice(5), ...HERO_VIDEOS.slice(0, 5)].reverse();

// Individual video slot — only attaches `src` once it enters the viewport.
// Massive mobile perf win: 44 hero videos → only 2-3 actually load.
function LazyVideo({ src }) {
  const wrapRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    // Skip observer entirely once loaded — freeze the src attached state.
    if (visible) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "150px", threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  return (
    <div
      ref={wrapRef}
      className="aspect-[9/16] w-full shrink-0 overflow-hidden rounded-2xl border border-white/60 bg-[#F2F6FB] shadow-[0_18px_40px_-18px_rgba(15,23,42,0.4)]"
    >
      {visible && (
        <video
          src={src}
          muted
          autoPlay
          loop
          playsInline
          preload="auto"
          className="h-full w-full object-cover"
        />
      )}
    </div>
  );
}

function VideoColumn({ duration, videos, reverse }) {
  const loop = [...videos, ...videos];
  return (
    <div className="relative h-full overflow-hidden">
      <div
        className="animate-vscroll flex flex-col gap-4"
        style={{ animationDuration: `${duration}s`, animationDirection: reverse ? "reverse" : "normal" }}
      >
        {loop.map((src, i) => (
          <LazyVideo key={i} src={src} />
        ))}
      </div>
      {/* fade top / bottom so the loop blends into the page */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white to-transparent" />
    </div>
  );
}

export default function HeroVideos() {
  return (
    <div
      aria-hidden="true"
      data-testid="hero-video-columns"
      className="pointer-events-none absolute inset-0 -z-0 hidden overflow-hidden md:block"
    >
      {/* left diagonal column — leans inward, scrolls down */}
      <div className="absolute -left-10 top-1/2 h-[150%] w-[110px] -translate-y-1/2 rotate-[12deg] opacity-95 sm:w-[140px] lg:w-[168px]">
        <VideoColumn duration={36} videos={LEFT_VIDEOS} reverse={false} />
      </div>
      {/* right diagonal column — mirror tilt, different order, scrolls up */}
      <div className="absolute -right-10 top-1/2 h-[150%] w-[110px] -translate-y-1/2 rotate-[-12deg] opacity-95 sm:w-[140px] lg:w-[168px]">
        <VideoColumn duration={30} videos={RIGHT_VIDEOS} reverse={true} />
      </div>
      {/* soft white side fades to protect headline readability */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-[35%] bg-gradient-to-r from-white via-white/70 to-transparent sm:w-[25%] sm:via-white/55 lg:w-[20%]" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-[35%] bg-gradient-to-l from-white via-white/70 to-transparent sm:w-[25%] sm:via-white/55 lg:w-[20%]" />
    </div>
  );
}
